import type { SessionStore, StoredSession } from '@sunucupark/client-core';

/** How other tabs learn that this one signed in or out. */
const CHANNEL = 'postamerkezi:session';

/**
 * The browser keeps the access token in memory only: the refresh token lives in
 * the `HttpOnly` cookie, so a reload renews from the cookie and script running
 * on the page can never read a long-lived credential (ADR 0007). Tabs tell each
 * other about a sign-in or sign-out over a BroadcastChannel — the token itself
 * never travels with it.
 */
export class MemorySessionStore implements SessionStore {
  private session: StoredSession | null = null;
  private readonly listeners = new Set<(session: StoredSession | null) => void>();
  private readonly channel: BroadcastChannel | null;

  constructor() {
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<{ signedIn: boolean }>) => {
        if (!event.data.signedIn) this.session = null;
        for (const listener of this.listeners) listener(this.session);
      };
    }
  }

  async read(): Promise<StoredSession | null> {
    return this.session;
  }

  async write(session: StoredSession): Promise<void> {
    const wasSignedOut = this.session === null;
    this.session = session;
    if (wasSignedOut) this.channel?.postMessage({ signedIn: true });
  }

  async clear(): Promise<void> {
    this.session = null;
    this.channel?.postMessage({ signedIn: false });
  }

  subscribe(listener: (session: StoredSession | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Closes the channel; used when the app unmounts in a test. */
  dispose(): void {
    this.channel?.close();
  }
}
